import { useEffect, useState, useRef } from 'react'
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion'
import { 
  Phone, 
  MapPin, 
  Clock, 
  Instagram, 
  Facebook, 
  ChevronRight,
  Star,
  Calendar,
  Users,
  Wine,
  UtensilsCrossed,
  ChefHat
} from 'lucide-react'

// Scroll reveal hook
function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.unobserve(entry.target)
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer.disconnect()
  }, [])

  return { ref, isVisible }
}

// Navigation
function Navigation() {
  const [scrolled, setScrolled] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navLinks = [
    { name: 'Menu', href: '#menu' },
    { name: 'Our Story', href: '#about' },
    { name: 'Reservations', href: '#reservations' },
    { name: 'Private Dining', href: '#private' },
    { name: 'Contact', href: '#contact' },
  ]

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled ? 'glass-nav py-3' : 'bg-transparent py-6'
    }`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <a href="#" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold-400 to-gold-500 flex items-center justify-center">
              <span className="text-wine-900 font-serif font-bold text-lg">M</span>
            </div>
            <div className="hidden sm:block">
              <h1 className="font-serif text-xl text-cream-100">Trattoria Milano</h1>
              <p className="text-xs text-gold-400/70 tracking-widest uppercase">Est. 1987</p>
            </div>
          </a>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-sm text-cream-200/80 hover:text-gold-400 transition-colors duration-300 tracking-wide"
              >
                {link.name}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-4">
            <a
              href="tel:+61391234567"
              className="hidden md:flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-gold-500 to-gold-400 text-wine-900 rounded-full text-sm font-medium hover:shadow-lg hover:shadow-gold-400/20 transition-all duration-300"
            >
              <Phone className="w-4 h-4" />
              <span>Book Now</span>
            </a>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-cream-100"
            >
              <div className="w-6 h-5 flex flex-col justify-between">
                <span className={`w-full h-0.5 bg-current transition-transform duration-300 ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`} />
                <span className={`w-full h-0.5 bg-current transition-opacity duration-300 ${mobileMenuOpen ? 'opacity-0' : ''}`} />
                <span className={`w-full h-0.5 bg-current transition-transform duration-300 ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass-nav border-t border-gold-400/10"
          >
            <div className="px-6 py-6 space-y-4">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-lg text-cream-100 hover:text-gold-400 transition-colors"
                >
                  {link.name}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  )
}

// Hero Section
function Hero() {
  const { scrollY } = useScroll()
  const y1 = useTransform(scrollY, [0, 500], [0, 150])
  const y2 = useTransform(scrollY, [0, 500], [0, -100])
  const opacity = useTransform(scrollY, [0, 400], [1, 0])

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden hero-pattern">
      {/* Background Elements */}
      <motion.div style={{ y: y1 }} className="absolute inset-0 parallax-layer">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gold-400/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-wine-500/20 rounded-full blur-3xl" />
      </motion.div>

      {/* Floating decorative elements */}
      <motion.div 
        style={{ y: y2 }}
        className="absolute top-1/4 right-20 w-32 h-32 border border-gold-400/20 rounded-full animate-float hidden lg:block"
      />
      <motion.div 
        style={{ y: y2 }}
        className="absolute bottom-1/3 left-16 w-24 h-24 border border-gold-400/10 rounded-full animate-float hidden lg:block"
        transition={{ delay: 2 }}
      />

      <motion.div style={{ opacity }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="text-gold-400 text-sm tracking-[0.3em] uppercase mb-6">Authentic Italian Cuisine</p>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-cream-100 mb-6 leading-tight">
            Trattoria
            <span className="block text-gradient">Milano</span>
          </h1>
          <p className="text-cream-200/70 text-lg md:text-xl max-w-2xl mx-auto mb-10 font-light leading-relaxed">
            Experience the soul of Italy in the heart of Melbourne. 
            Handmade pasta, wood-fired pizzas, and an award-winning wine selection.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <a
            href="#reservations"
            className="group px-8 py-4 bg-gradient-to-r from-gold-500 to-gold-400 text-wine-900 rounded-full font-medium text-sm tracking-wide hover:shadow-xl hover:shadow-gold-400/30 transition-all duration-300 flex items-center gap-2"
          >
            <Calendar className="w-4 h-4" />
            Reserve a Table
            <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href="#menu"
            className="px-8 py-4 border border-gold-400/30 text-gold-400 rounded-full font-medium text-sm tracking-wide hover:bg-gold-400/10 transition-all duration-300"
          >
            View Menu
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.6 }}
          className="mt-16 flex items-center justify-center gap-8 text-cream-200/50 text-sm"
        >
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            <span>Little Bourke St, Melbourne</span>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>Open Tue - Sun</span>
          </div>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="w-6 h-10 border-2 border-gold-400/30 rounded-full flex justify-center">
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1.5 h-1.5 bg-gold-400 rounded-full mt-2"
          />
        </div>
      </motion.div>
    </section>
  )
}

// Menu Section
function MenuSection() {
  const { ref, isVisible } = useScrollReveal()

  const menuCategories = [
    {
      name: 'Antipasti',
      items: [
        { name: 'Burrata e Pomodoro', desc: 'Fresh burrata, heirloom tomatoes, basil oil, aged balsamic', price: '24' },
        { name: 'Carpaccio di Manzo', desc: 'Thinly sliced beef, truffle oil, parmesan, arugula', price: '26' },
        { name: 'Arancini Siciliani', desc: 'Crispy risotto balls, mozzarella, saffron aioli', price: '18' },
        { name: 'Bruschetta Classica', desc: 'Grilled sourdough, tomato, garlic, basil, EVOO', price: '16' },
      ]
    },
    {
      name: 'Primi Piatti',
      items: [
        { name: 'Tagliatelle al Tartufo', desc: 'Handmade pasta, black truffle, parmesan cream', price: '38' },
        { name: 'Linguine alle Vongole', desc: 'Clams, white wine, garlic, chili, parsley', price: '34' },
        { name: 'Risotto ai Funghi', desc: 'Arborio rice, porcini mushrooms, truffle oil', price: '32' },
        { name: 'Gnocchi di Patate', desc: 'Potato gnocchi, gorgonzola, walnuts, sage', price: '30' },
      ]
    },
    {
      name: 'Secondi',
      items: [
        { name: 'Osso Buco alla Milanese', desc: 'Braised veal shank, saffron risotto, gremolata', price: '48' },
        { name: 'Branzino al Sale', desc: 'Whole sea bass baked in salt crust, herbs, lemon', price: '52' },
        { name: 'Bistecca Fiorentina', desc: 'T-bone steak, rosemary potatoes, charred lemon (for 2)', price: '95' },
        { name: 'Agnello Scottadito', desc: 'Grilled lamb chops, garlic, rosemary, roasted vegetables', price: '46' },
      ]
    },
    {
      name: 'Dolci',
      items: [
        { name: 'Tiramisù della Casa', desc: 'Classic Italian dessert, mascarpone, espresso, cocoa', price: '16' },
        { name: 'Panna Cotta ai Frutti', desc: 'Vanilla bean panna cotta, berry compote', price: '15' },
        { name: 'Cannoli Siciliani', desc: 'Crispy shells, ricotta, pistachios, chocolate', price: '14' },
        { name: 'Torta Caprese', desc: 'Flourless chocolate almond cake, vanilla gelato', price: '17' },
      ]
    },
  ]

  return (
    <section id="menu" className="py-24 lg:py-32 relative" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className={`text-center mb-16 scroll-reveal ${isVisible ? 'revealed' : ''}`}>
          <p className="text-gold-400 text-sm tracking-[0.3em] uppercase mb-4">Our Menu</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-cream-100 mb-6">
            A Taste of <span className="text-gradient">Italy</span>
          </h2>
          <p className="text-cream-200/60 max-w-2xl mx-auto text-lg">
            Each dish is crafted with passion using authentic Italian recipes and the finest local ingredients.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {menuCategories.map((category, idx) => (
            <div 
              key={category.name}
              className={`scroll-reveal stagger-${idx + 1} ${isVisible ? 'revealed' : ''}`}
            >
              <div className="glass-card rounded-2xl p-8 lg:p-10">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 rounded-full bg-gold-400/10 flex items-center justify-center">
                    <UtensilsCrossed className="w-5 h-5 text-gold-400" />
                  </div>
                  <h3 className="font-serif text-2xl text-cream-100">{category.name}</h3>
                </div>

                <div className="space-y-6">
                  {category.items.map((item) => (
                    <div key={item.name} className="menu-item group">
                      <div className="flex items-baseline justify-between gap-4">
                        <h4 className="text-cream-100 font-medium text-lg">{item.name}</h4>
                        <div className="menu-line flex-1 border-b border-gold-400/10 border-dashed transition-colors" />
                        <span className="menu-price text-gold-400 font-serif text-xl transition-all">${item.price}</span>
                      </div>
                      <p className="text-cream-200/50 text-sm mt-1">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className={`mt-16 text-center scroll-reveal stagger-4 ${isVisible ? 'revealed' : ''}`}>
          <p className="text-cream-200/40 text-sm mb-4">* Menu items and prices subject to seasonal availability</p>
          <a
            href="#"
            className="inline-flex items-center gap-2 text-gold-400 hover:text-gold-300 transition-colors"
          >
            <Wine className="w-4 h-4" />
            <span>View Wine List</span>
            <ChevronRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  )
}

// About Section
function AboutSection() {
  const { ref, isVisible } = useScrollReveal()

  return (
    <section id="about" className="py-24 lg:py-32 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 bg-gradient-to-b from-wine-800/30 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className={`scroll-reveal ${isVisible ? 'revealed' : ''}`}>
            <p className="text-gold-400 text-sm tracking-[0.3em] uppercase mb-4">Our Story</p>
            <h2 className="font-serif text-4xl md:text-5xl text-cream-100 mb-6">
              A Family Legacy of <span className="text-gradient">Italian Passion</span>
            </h2>
            <div className="space-y-4 text-cream-200/70 leading-relaxed">
              <p>
                Founded in 1987 by Giovanni and Maria Rossi, Trattoria Milano began as a small family 
                restaurant with a simple mission: to share the authentic flavors of their hometown 
                with the people of Melbourne.
              </p>
              <p>
                Today, three decades later, their children Marco and Sophia continue the tradition, 
                combining time-honored recipes passed down through generations with the finest 
                Victorian produce.
              </p>
              <p>
                Our pasta is still made fresh daily by hand. Our wood-fired oven was imported 
                directly from Naples. And our commitment to hospitality remains unchanged: 
                every guest is family.
              </p>
            </div>

            <div className="mt-8 flex items-center gap-8">
              <div className="text-center">
                <p className="font-serif text-4xl text-gold-400">37</p>
                <p className="text-cream-200/50 text-sm">Years of Excellence</p>
              </div>
              <div className="w-px h-12 bg-gold-400/20" />
              <div className="text-center">
                <p className="font-serif text-4xl text-gold-400">3</p>
                <p className="text-cream-200/50 text-sm">Generations</p>
              </div>
              <div className="w-px h-12 bg-gold-400/20" />
              <div className="text-center">
                <p className="font-serif text-4xl text-gold-400">15k+</p>
                <p className="text-cream-200/50 text-sm">Happy Guests</p>
              </div>
            </div>
          </div>

          <div className={`scroll-reveal stagger-2 ${isVisible ? 'revealed' : ''}`}>
            <div className="relative">
              <div className="glass-card rounded-2xl p-2 gold-glow">
                <div className="aspect-[4/5] rounded-xl overflow-hidden bg-wine-800">
                  <div className="w-full h-full flex items-center justify-center text-cream-200/30">
                    <div className="text-center">
                      <ChefHat className="w-16 h-16 mx-auto mb-4" />
                      <p className="text-sm">Restaurant Interior</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating badge */}
              <div className="absolute -bottom-6 -left-6 glass-card rounded-xl p-4 gold-glow">
                <div className="flex items-center gap-3">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-gold-400 fill-gold-400" />
                    ))}
                  </div>
                  <div>
                    <p className="text-cream-100 font-medium text-sm">Google Reviews</p>
                    <p className="text-cream-200/50 text-xs">4.9 out of 5</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Team Section */}
        <div className={`mt-24 scroll-reveal stagger-3 ${isVisible ? 'revealed' : ''}`}>
          <div className="text-center mb-12">
            <h3 className="font-serif text-3xl text-cream-100 mb-4">Meet the Family</h3>
            <p className="text-cream-200/60">The passionate team behind every dish</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: 'Marco Rossi', role: 'Head Chef', exp: '20+ years' },
              { name: 'Sophia Rossi', role: 'Restaurant Manager', exp: '15+ years' },
              { name: 'Antonio Bianchi', role: 'Sous Chef', exp: '12+ years' },
              { name: 'Isabella Romano', role: 'Sommelier', exp: '10+ years' },
            ].map((person) => (
              <div key={person.name} className="glass-card rounded-xl p-6 text-center hover:border-gold-400/30 transition-colors">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-gold-400/20 to-wine-600/20 flex items-center justify-center">
                  <span className="font-serif text-2xl text-gold-400">{person.name[0]}</span>
                </div>
                <h4 className="text-cream-100 font-medium">{person.name}</h4>
                <p className="text-gold-400 text-sm">{person.role}</p>
                <p className="text-cream-200/40 text-xs mt-1">{person.exp}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

// Reservations Section
function ReservationsSection() {
  const { ref, isVisible } = useScrollReveal()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    guests: '2',
    occasion: '',
    requests: ''
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    alert('Thank you for your reservation request! We will confirm shortly.')
  }

  return (
    <section id="reservations" className="py-24 lg:py-32 relative" ref={ref}>
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className={`text-center mb-12 scroll-reveal ${isVisible ? 'revealed' : ''}`}>
          <p className="text-gold-400 text-sm tracking-[0.3em] uppercase mb-4">Reservations</p>
          <h2 className="font-serif text-4xl md:text-5xl text-cream-100 mb-6">
            Book Your <span className="text-gradient">Table</span>
          </h2>
          <p className="text-cream-200/60 max-w-xl mx-auto">
            Join us for an unforgettable dining experience. For parties larger than 8, 
            please contact us directly.
          </p>
        </div>

        <div className={`glass-card rounded-2xl p-8 lg:p-12 scroll-reveal stagger-2 ${isVisible ? 'revealed' : ''}`}>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-cream-200/70 text-sm mb-2">Full Name</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 bg-wine-800/50 border border-gold-400/20 rounded-xl text-cream-100 placeholder-cream-200/30 focus:border-gold-400 transition-colors"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-cream-200/70 text-sm mb-2">Email</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 bg-wine-800/50 border border-gold-400/20 rounded-xl text-cream-100 placeholder-cream-200/30 focus:border-gold-400 transition-colors"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <label className="block text-cream-200/70 text-sm mb-2">Date</label>
                <input
                  type="date"
                  required
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  className="w-full px-4 py-3 bg-wine-800/50 border border-gold-400/20 rounded-xl text-cream-100 focus:border-gold-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-cream-200/70 text-sm mb-2">Time</label>
                <select
                  required
                  value={formData.time}
                  onChange={(e) => setFormData({...formData, time: e.target.value})}
                  className="w-full px-4 py-3 bg-wine-800/50 border border-gold-400/20 rounded-xl text-cream-100 focus:border-gold-400 transition-colors"
                >
                  <option value="">Select time</option>
                  <option value="17:30">5:30 PM</option>
                  <option value="18:00">6:00 PM</option>
                  <option value="18:30">6:30 PM</option>
                  <option value="19:00">7:00 PM</option>
                  <option value="19:30">7:30 PM</option>
                  <option value="20:00">8:00 PM</option>
                  <option value="20:30">8:30 PM</option>
                  <option value="21:00">9:00 PM</option>
                </select>
              </div>
              <div>
                <label className="block text-cream-200/70 text-sm mb-2">
                  <Users className="w-4 h-4 inline mr-1" />
                  Guests
                </label>
                <select
                  value={formData.guests}
                  onChange={(e) => setFormData({...formData, guests: e.target.value})}
                  className="w-full px-4 py-3 bg-wine-800/50 border border-gold-400/20 rounded-xl text-cream-100 focus:border-gold-400 transition-colors"
                >
                  {[1,2,3,4,5,6,7,8].map(n => (
                    <option key={n} value={n}>{n} {n === 1 ? 'Guest' : 'Guests'}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-cream-200/70 text-sm mb-2">Special Occasion</label>
              <select
                value={formData.occasion}
                onChange={(e) => setFormData({...formData, occasion: e.target.value})}
                className="w-full px-4 py-3 bg-wine-800/50 border border-gold-400/20 rounded-xl text-cream-100 focus:border-gold-400 transition-colors"
              >
                <option value="">None</option>
                <option value="birthday">Birthday</option>
                <option value="anniversary">Anniversary</option>
                <option value="date">Date Night</option>
                <option value="business">Business Dinner</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-cream-200/70 text-sm mb-2">Special Requests</label>
              <textarea
                rows={3}
                value={formData.requests}
                onChange={(e) => setFormData({...formData, requests: e.target.value})}
                className="w-full px-4 py-3 bg-wine-800/50 border border-gold-400/20 rounded-xl text-cream-100 placeholder-cream-200/30 focus:border-gold-400 transition-colors resize-none"
                placeholder="Allergies, dietary requirements, seating preferences..."
              />
            </div>

            <button
              type="submit"
              className="w-full py-4 bg-gradient-to-r from-gold-500 to-gold-400 text-wine-900 rounded-xl font-medium text-sm tracking-wide hover:shadow-xl hover:shadow-gold-400/30 transition-all duration-300"
            >
              Request Reservation
            </button>
          </form>

          <div className="mt-8 pt-8 border-t border-gold-400/10 text-center">
            <p className="text-cream-200/50 text-sm">
              Prefer to call? <a href="tel:+61391234567" className="text-gold-400 hover:text-gold-300">(03) 9123 4567</a>
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

// Private Dining Section
function PrivateDiningSection() {
  const { ref, isVisible } = useScrollReveal()

  return (
    <section id="private" className="py-24 lg:py-32 relative bg-wine-800/30" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className={`order-2 lg:order-1 scroll-reveal ${isVisible ? 'revealed' : ''}`}>
            <div className="glass-card rounded-2xl p-2 gold-glow">
              <div className="aspect-video rounded-xl overflow-hidden bg-wine-800">
                <div className="w-full h-full flex items-center justify-center text-cream-200/30">
                  <div className="text-center">
                    <Users className="w-16 h-16 mx-auto mb-4" />
                    <p className="text-sm">Private Dining Room</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={`order-1 lg:order-2 scroll-reveal stagger-2 ${isVisible ? 'revealed' : ''}`}>
            <p className="text-gold-400 text-sm tracking-[0.3em] uppercase mb-4">Private Dining</p>
            <h2 className="font-serif text-4xl md:text-5xl text-cream-100 mb-6">
              Exclusive <span className="text-gradient">Events</span>
            </h2>
            <p className="text-cream-200/70 leading-relaxed mb-6">
              Host your next celebration in our elegant private dining room. 
              Perfect for corporate events, birthdays, anniversaries, and intimate gatherings.
            </p>
            
            <div className="space-y-4 mb-8">
              {[
                'Private dining room seats up to 24 guests',
                'Customizable menus with chef consultation',
                'Dedicated waitstaff and sommelier service',
                'Audio-visual equipment available',
                'Personalized wine pairings',
              ].map((feature, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-gold-400/10 flex items-center justify-center mt-0.5">
                    <ChevronRight className="w-3 h-3 text-gold-400" />
                  </div>
                  <p className="text-cream-200/70">{feature}</p>
                </div>
              ))}
            </div>

            <a
              href="mailto:events@trattoriamilano.com.au"
              className="inline-flex items-center gap-2 px-8 py-4 border border-gold-400/30 text-gold-400 rounded-full hover:bg-gold-400/10 transition-all"
            >
              Enquire About Private Dining
              <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}

// Contact/Footer Section
function Footer() {
  return (
    <footer id="contact" className="py-16 lg:py-24 border-t border-gold-400/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold-400 to-gold-500 flex items-center justify-center">
                <span className="text-wine-900 font-serif font-bold text-lg">M</span>
              </div>
              <div>
                <h3 className="font-serif text-xl text-cream-100">Trattoria Milano</h3>
                <p className="text-xs text-gold-400/70 tracking-widest uppercase">Est. 1987</p>
              </div>
            </div>
            <p className="text-cream-200/50 text-sm leading-relaxed">
              Authentic Italian cuisine in the heart of Melbourne. 
              Family-owned and operated since 1987.
            </p>
          </div>

          {/* Hours */}
          <div>
            <h4 className="text-cream-100 font-medium mb-4">Opening Hours</h4>
            <div className="space-y-2 text-sm text-cream-200/50">
              <p>Tuesday - Thursday: 5:30pm - 10pm</p>
              <p>Friday - Saturday: 5:30pm - 11pm</p>
              <p>Sunday: 5pm - 9:30pm</p>
              <p className="text-gold-400/70">Monday: Closed</p>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-cream-100 font-medium mb-4">Contact</h4>
            <div className="space-y-3 text-sm">
              <a href="https://maps.google.com" className="flex items-start gap-2 text-cream-200/50 hover:text-gold-400 transition-colors">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>123 Little Bourke Street<br />Melbourne VIC 3000</span>
              </a>
              <a href="tel:+61391234567" className="flex items-center gap-2 text-cream-200/50 hover:text-gold-400 transition-colors">
                <Phone className="w-4 h-4 flex-shrink-0" />
                <span>(03) 9123 4567</span>
              </a>
              <a href="mailto:info@trattoriamilano.com.au" className="flex items-center gap-2 text-cream-200/50 hover:text-gold-400 transition-colors">
                <span className="w-4 h-4 flex-shrink-0">@</span>
                <span>info@trattoriamilano.com.au</span>
              </a>
            </div>
          </div>

          {/* Social */}
          <div>
            <h4 className="text-cream-100 font-medium mb-4">Follow Us</h4>
            <div className="flex gap-3">
              <a href="#" className="w-10 h-10 rounded-full border border-gold-400/20 flex items-center justify-center text-cream-200/50 hover:text-gold-400 hover:border-gold-400/40 transition-all">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full border border-gold-400/20 flex items-center justify-center text-cream-200/50 hover:text-gold-400 hover:border-gold-400/40 transition-all">
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-gold-400/10 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-cream-200/30">
          <p>&copy; 2024 Trattoria Milano. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="#" className="hover:text-gold-400 transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-gold-400 transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  )
}

// Main App
function App() {
  return (
    <div className="min-h-screen bg-wine-900">
      <Navigation />
      <main>
        <Hero />
        <MenuSection />
        <AboutSection />
        <ReservationsSection />
        <PrivateDiningSection />
      </main>
      <Footer />
    </div>
  )
}

export default App
